import { Container } from '@mui/material';
import {
  ContentContainer,
  Back,
  PageTitle,
  StyledTextField,
  palette,
} from '@src/core/styles';
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FieldGroupWrapper } from '../login-page/login-page.styles';
import { TitleWrapper } from '../offers-form/offers-form.styles';
import { DescriptionText } from '../price-page/price-page.styles';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Arrow } from '@src/core/icons/arrow.svg';
import { ReactComponent as Check } from '@src/core/icons/check.svg';
import { joiResolver } from '@hookform/resolvers/joi';
import type {
  SupportErrorResponse,
  SupportErrors,
  SupportRequestParams,
} from '@src/core/types';
import { supportSchema } from '@src/core/schemas';
import { Button } from '@src/components';
import { FormContainer } from '../route-management-page/components/form-component/form-components.styles';
import { Support } from '@src/core/services';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';

export const HelpForm: FC = () => {
  const navigate = useNavigate();
  const [errorsRequest, setErrorsRequest] = useState<SupportErrors>();
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<SupportRequestParams>({
    mode: 'onChange',
    resolver: joiResolver(supportSchema),
    defaultValues: {
      name: '',
      email: '',
      message: '',
    },
  });

  const { mutate } = useMutation(Support.help, {
    onSuccess: (response) => {
      enqueueSnackbar('Сообщение отправлено', {
        variant: 'reportComplete',
        className: 'success',
        preventDuplicate: true,
      });
    },
    onError: (error: AxiosError<SupportErrorResponse>) => {
      setErrorsRequest(error.response?.data.errors);
    },
  });

  const backHandler = () => {
    navigate(-1);
  };

  const onSubmit = (data: SupportRequestParams) => {
    mutate(data);
  };

  const changeHandler = () => {
    setErrorsRequest(undefined);
  };

  return (
    <Container>
      <ContentContainer>
        <FormContainer>
          <Back onClick={backHandler}>
            <Arrow />
          </Back>
          <TitleWrapper>
            <PageTitle>Вопрос по работе сервиса</PageTitle>
            <DescriptionText>
              Задайте вопросы по функциональности и возможностям сервиса, и
              укажите данные для обратной связи
            </DescriptionText>
          </TitleWrapper>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FieldGroupWrapper>
              <Controller
                control={control}
                name="name"
                render={({ field: { value, onBlur, onChange } }) => (
                  <StyledTextField
                    onBlur={onBlur}
                    variant="outlined"
                    label="Имя"
                    autoComplete="on"
                    InputLabelProps={{
                      sx: {
                        '&.Mui-focused': {
                          color: `${palette.colors.green_base}`,
                        },
                        '&.Mui-error': {
                          color: `${palette.colors.error}`,
                        },
                      },
                    }}
                    value={value}
                    error={!!errors?.name?.message || !!errorsRequest?.name}
                    helperText={errors?.name?.message || errorsRequest?.name}
                    onChange={(e) => {
                      changeHandler();
                      onChange(e);
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ field: { value, onBlur, onChange } }) => (
                  <StyledTextField
                    onBlur={onBlur}
                    variant="outlined"
                    label="Электронная почта"
                    placeholder="Электронная почта"
                    autoComplete="on"
                    InputLabelProps={{
                      sx: {
                        '&.Mui-focused': {
                          color: `${palette.colors.green_base}`,
                        },
                        '&.Mui-error': { color: `${palette.colors.error}` },
                      },
                    }}
                    value={value}
                    error={!!errors?.email?.message || !!errorsRequest?.email}
                    helperText={errors?.email?.message || errorsRequest?.email}
                    onChange={(e) => {
                      changeHandler();
                      onChange(e);
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="message"
                render={({ field: { value, onBlur, onChange } }) => (
                  <StyledTextField
                    onBlur={onBlur}
                    variant="outlined"
                    label="Задайте интересующий вас вопрос"
                    multiline
                    inputProps={{ maxLength: 1000 }}
                    autoComplete="on"
                    rows={5}
                    InputLabelProps={{
                      sx: {
                        '&.Mui-focused': {
                          color: `${palette.colors.green_base}`,
                        },
                        '&.Mui-error': { color: `${palette.colors.error}` },
                      },
                    }}
                    value={value}
                    error={
                      !!errors?.message?.message || !!errorsRequest?.message
                    }
                    helperText={
                      errors?.message?.message || errorsRequest?.message
                    }
                    onChange={(e) => {
                      changeHandler();
                      onChange(e);
                    }}
                  />
                )}
              />
            </FieldGroupWrapper>
            <Button
              text="Отправить"
              type="submit"
              variant="contained"
              icon={<Check />}
              borderradius="L"
              disabled={!isValid}
            />
          </form>
        </FormContainer>
      </ContentContainer>
    </Container>
  );
};
